/**
 * Inkex/lib2geom-style cut length: bake path with full ancestor matrix, then measure in JS.
 * Uses transformPathDataWithMatrix (relative round-trip) so h/v/z semantics match lib2geom,
 * not local getTotalLength() × a single scale factor.
 */

import SVGPathCommander from 'svg-path-commander';
import { transformPathDataWithMatrix, type SvgMatrixLike } from './svgPathTransform';

function isNearIdentity(matrix: SvgMatrixLike, eps = 1e-6): boolean {
  return (
    Math.abs(matrix.a - 1) < eps &&
    Math.abs(matrix.d - 1) < eps &&
    Math.abs(matrix.b) < eps &&
    Math.abs(matrix.c) < eps &&
    Math.abs(matrix.e) < eps &&
    Math.abs(matrix.f) < eps
  );
}

function measureBakedSubpathLength(subpath: string, matrix: SvgMatrixLike): number {
  const baked = transformPathDataWithMatrix(subpath, matrix);
  if (!baked) return 0;
  return SVGPathCommander.getTotalLength(baked);
}

/** Cut length of path `d` in root SVG user units after applying matrix. */
export function measureTransformedPathLength(pathData: string, matrix: SvgMatrixLike): number {
  if (!pathData?.trim()) return 0;

  try {
    if (isNearIdentity(matrix)) {
      return SVGPathCommander.getTotalLength(pathData);
    }
    // Transform each subpath independently. PDF/Inkscape compound paths reuse relative
    // cursors across moveto segments; baking the full `d` at once corrupts length.
    const subpaths = SVGPathCommander.splitPath(pathData);
    if (subpaths.length <= 1) {
      return measureBakedSubpathLength(pathData, matrix);
    }
    let total = 0;
    for (const subpath of subpaths) {
      const normalized =
        typeof subpath === 'string' ? subpath : SVGPathCommander.pathToString(subpath);
      total += measureBakedSubpathLength(normalized, matrix);
    }
    return total;
  } catch {
    return 0;
  }
}
